/**
 * The single traffic light for a probed model.
 *
 * A model can be judged two ways, and until now they were reported separately:
 * `evaluateAgainstProfile` scores it against an AR delivery budget (file size,
 * vertices, textures), while `collectIssues` reports facts intrinsic to the file
 * (no active scene, a required extension we can't vouch for, NPOT textures).
 * Both matter, and a creator reading only the first can see "Ready for AR" on a
 * model that won't open — the budget axes are all green because a blocking
 * problem isn't a budget problem.
 *
 * This module folds the two into one status, so there is exactly one answer to
 * "can I ship this?" and it can be shown without scrolling past it.
 */

import type {
  ModelIssue,
  ModelProbeReport,
  ModelSummary,
  OptimizationProfile,
  SummaryReason,
  VerdictStatus,
} from './types';
import { collectIssues, worstSeverity } from './issues';
import { evaluateAgainstProfile } from './profiles';

/** An issue's severity as a traffic-light colour: info is not a defect. */
function statusOfIssues(issues: ModelIssue[]): VerdictStatus {
  const worst = worstSeverity(issues);
  if (worst === 'error') return 'red';
  if (worst === 'warning') return 'yellow';
  return 'green';
}

function worstOf(a: VerdictStatus, b: VerdictStatus): VerdictStatus {
  if (a === 'red' || b === 'red') return 'red';
  if (a === 'yellow' || b === 'yellow') return 'yellow';
  return 'green';
}

/**
 * Combine budget verdict and intrinsic issues into one status plus the short
 * list worth showing beside it.
 *
 * The status is the worse of the two inputs — never the budget alone. Being
 * under every budget says nothing about whether the file opens.
 */
export function summarizeModel(report: ModelProbeReport, profile: OptimizationProfile): ModelSummary {
  const verdict = evaluateAgainstProfile(report, profile);
  const issues = collectIssues(report);
  const status = worstOf(verdict.overall, statusOfIssues(issues));

  return {
    status,
    reason: reasonFor(report, verdict.overall, issues),
    verdict,
    issues,
    // Info-level notes are advisory; they belong in the detail list, not next
    // to the light, or the "what must I fix" list stops being scannable.
    highlights: issues.filter((i) => i.severity !== 'info'),
  };
}

function reasonFor(
  report: ModelProbeReport,
  budgetStatus: VerdictStatus,
  issues: ModelIssue[],
): SummaryReason {
  if (report.bailout) return 'unreadable';
  if (issues.some((i) => i.severity === 'error')) return 'blocking-issues';
  if (budgetStatus !== 'green') return 'over-budget';
  if (issues.some((i) => i.severity === 'warning')) return 'quality-warnings';
  return 'ready';
}
